import { AfterViewInit, Component, computed, effect, signal, viewChild } from "@angular/core";
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from "@angular/forms";
import { MatNativeDateModule } from "@angular/material/core";
import { MatCalendar, MatDatepickerModule } from "@angular/material/datepicker";
import { parse as parseDate, getHours, getMinutes, setHours, setMinutes, min } from "date-fns";

@Component({
  selector: "hawk-shared-date-time-control",
  template: `
    <mat-calendar
      [startAt]="value()"
      [selected]="value()"
      (selectedChange)="handleDateChange($event)"
      class="min-w-xs"
    />
    <div class="bm-join w-full px-2 pb-2">
      <select
        class="bm-select bm-join-item"
        [ngModel]="hours()"
        (ngModelChange)="handleHoursChanged($event)"
        [disabled]="!value()"
      >
        @for (option of minuteOptions; track $index) {
          <option [value]="option.value">{{ option.label }}</option>
        }
      </select>
      <select
        class="bm-select bm-join-item"
        [ngModel]="minutes()"
        (ngModelChange)="handleMinutesChanged($event)"
        [disabled]="!value()"
      >
        @for (option of minuteOptions; track $index) {
          <option [value]="option.value">{{ option.label }}</option>
        }
      </select>
    </div>
  `,
  host: {
    class: "block bg-base-100 rounded-md",
  },
  imports: [MatNativeDateModule, MatDatepickerModule, FormsModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: DateTimeControlComponent,
    },
  ],
})
export class DateTimeControlComponent implements ControlValueAccessor, AfterViewInit {
  protected value = signal<string | Date>(null);
  protected hours = computed(() => getHours(this.value()));
  protected minutes = computed(() => getMinutes(this.value()));
  protected calendar = viewChild(MatCalendar);

  protected onChange = (value: unknown) => {};
  protected onTouched = () => {};
  protected touched = false;
  protected disabled = false;

  protected hourOptions = Array.from({ length: 24 }, (_, i) => {
    return {
      label: i.toString().padStart(2, "0"),
      value: i,
    };
  });
  protected minuteOptions = Array.from({ length: 60 }, (_, i) => {
    return {
      label: i.toString().padStart(2, "0"),
      value: i,
    };
  });

  public writeValue(value: any): void {
    if (!value) {
      value = new Date();
    }
    if (typeof value === "string") {
      value = new Date(value);
    }
    this.value.set(value);
    this.calendar()._goToDateInView(value, "month");
  }

  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  protected handleDateChange(value: string | Date) {
    value = setHours(value, getHours(this.value()));
    value = setMinutes(value, getMinutes(this.value()));
    this.value.set(value);
    this.onChange(value);
  }

  protected handleHoursChanged(hours: number) {
    const value = setHours(this.value(), hours);
    this.value.set(value);
    this.onChange(value);
  }

  protected handleMinutesChanged(minutes: number) {
    const value = setMinutes(this.value(), minutes);
    this.value.set(value);
    this.onChange(value);
  }

  public ngAfterViewInit(): void {
    this.calendar().updateTodaysDate();
  }
}
